// @flow
import * as React from 'react';
import { Provider } from './SessionContext';
import gql from 'graphql-tag.macro';
import client from 'apolloClient';

import {
  ENUM_RETAILER_LOG_ACTION,
  REQUEST_HEADERS,
  LOCAL_STORAGE_KEYS,
  UPDATE_TOKEN_TYPE,
} from 'constants/enums';
import { SnackbarContext } from 'contexts/SnackbarContext';
import { MUTATION_ADD_RETAILER_LOGS } from 'containers/useMutationAddRetailerLogs';
import {
  INVALID_OTP_PLEASE_TRY_AGAIN,
  ERROR_WHILE_LOGGING_IN,
} from 'constants/errors';

import type {
  LoginParams,
  RemoteLoginParams,
  SessionDetails,
} from 'types/session.js.flow';
type Props = {
  children: React.Node,
  client?: any,
};

type State = {
  resolved: boolean,
  loading: boolean,
  session: ?SessionDetails,
  error: any,
};

const OTP_AUTH_QUERY = gql`
  query otpAuth($input: OTPAuthInput!) {
    otpAuth(input: $input) {
      token
      refreshToken
    }
  }
`;

const JWT_AUTH_QUERY = gql`
  query jwtAuth($input: JWTAuthInput!) {
    jwtAuth(input: $input) {
      token
      refreshToken
    }
  }
`;

const UPDATE_TOKEN_MUTATION = gql`
  mutation updateTokens($input: TokenInput!) {
    updateTokens(tokenInput: $input) {
      token
      refreshToken
    }
  }
`;

const LOGOUT_MUTATION = gql`
  mutation logout {
    logout
  }
`;

export const clearStoredSession = () => localStorage.clear();

const storeSession = (session: SessionDetails) => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, session.token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, session.refreshToken);
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.REFRESHED_AT,
    `${session.refreshedAt}`
  );
};

const persistedSession = (): ?SessionDetails => {
  const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  const refreshToken =
    localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN) || '';
  const refreshedAt = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESHED_AT);
  if (!token) return null;

  return { token, refreshToken, refreshedAt: Number(refreshedAt) };
};

class SessionProvider extends React.Component<Props, State> {
  state = {
    resolved: false,
    loading: false,
    session: null,
    error: null,
    success: false,
  };

  static contextType = SnackbarContext;

  componentDidMount() {
    const session = persistedSession();
    const datetime = new Date().getTime();
    if (
      navigator &&
      navigator.onLine &&
      session &&
      session.refreshToken &&
      Math.abs(datetime - session.refreshedAt) > 3600000
    ) {
      this.setState({ loading: true });
      this.updateToken(session.refreshToken).then((newSession) => {
        this.setState({ session: newSession }, () => {
          this.setState({ resolved: true, loading: false });
        });
      });
    } else {
      this.setState({ session }, () => {
        this.setState({ resolved: true });
      });
    }
  }

  login = (variables: LoginParams) => {
    this.setState({ loading: true, error: null, session: null });

    return (this.props.client || client)
      .query({
        query: OTP_AUTH_QUERY,
        variables: { input: variables },
      })
      .then(({ data }) => {
        const authData = data ? data.otpAuth : null;

        if (!authData || !authData.token || !authData.refreshToken) {
          console.error(`Session: login ---  ${authData}`);
          throw new Error('No auth details found');
        }
        const datetime = new Date().getTime();
        const session: ?SessionDetails = {
          token: authData.token,
          refreshToken: authData.refreshToken,
          refreshedAt: datetime,
        };

        storeSession(session);
        client.resetStore();
        (this.props.client || client).mutate({
          mutation: MUTATION_ADD_RETAILER_LOGS,
          variables: {
            input: {
              action: ENUM_RETAILER_LOG_ACTION.LOGIN,
              userAgent: (navigator && navigator.userAgent) || '',
            },
          },
        });

        return this.setState({ session, loading: false, error: null }, () =>
          Promise.resolve(authData)
        );
      })
      .catch((error) => {
        this.setState({ error, loading: false, session: null });
        const { openSnackbar } = this.context;
        let errorText = ERROR_WHILE_LOGGING_IN;
        if (error && error.message.includes('Invalid OTP')) {
          errorText = INVALID_OTP_PLEASE_TRY_AGAIN;
        }
        openSnackbar &&
          openSnackbar({
            id: 'cy-error-on-otp-submit',
            message: errorText,
            autoHide: true,
          });
      });
  };

  remoteLogin = ({ token, xRetKey, phoneno, dialCode }: RemoteLoginParams) => {
    this.setState({ loading: true, error: null, session: null });

    return (this.props.client || client)
      .query({
        query: JWT_AUTH_QUERY,
        variables: {
          input: {
            phoneno,
            dialCode,
          },
        },
        context: {
          headers: {
            [REQUEST_HEADERS.X_RET_KEY]: `${xRetKey}`,
            [REQUEST_HEADERS.AUTHORIZATION]: token ? `Bearer ${token}` : '',
          },
        },
      })
      .then(({ data }) => {
        const authData = data ? data.jwtAuth : null;

        if (!authData || !authData.token || !authData.refreshToken) {
          console.error(`Session: remoteLogin ---  ${authData}`);
          throw new Error('No auth details found');
        }

        const datetime = new Date().getTime();
        const session: ?SessionDetails = {
          token: authData.token,
          refreshToken: authData.refreshToken,
          refreshedAt: datetime,
        };

        storeSession(session);
        client.resetStore();
        (this.props.client || client).mutate({
          mutation: MUTATION_ADD_RETAILER_LOGS,
          variables: {
            input: {
              action: ENUM_RETAILER_LOG_ACTION.LOGIN,
              userAgent: (navigator && navigator.userAgent) || '',
            },
          },
        });

        return this.setState({ session, loading: false, error: null }, () =>
          Promise.resolve(authData)
        );
      })
      .catch((error) => {
        this.setState({ error, loading: false, session: null });
        const { openSnackbar } = this.context;
        let errorText = ERROR_WHILE_LOGGING_IN;
        if (error && error.message.includes('Invalid OTP')) {
          errorText = INVALID_OTP_PLEASE_TRY_AGAIN;
        }
        openSnackbar &&
          openSnackbar({
            id: 'cy-error-on-otp-submit',
            message: errorText,
            autoHide: true,
          });
      });
  };

  updateToken = (refreshToken: string) => {
    return (this.props.client || client)
      .mutate({
        mutation: UPDATE_TOKEN_MUTATION,
        variables: {
          input: { refreshToken, type: UPDATE_TOKEN_TYPE.GRANT },
        },
      })
      .then(({ data }) => {
        const authData = data ? data.updateTokens : null;

        if (!authData || !authData.token || !authData.refreshToken) {
          console.error(`Session: updateToken ---  ${authData}`);
          throw new Error('No auth details found');
        }

        const datetime = new Date().getTime();
        const session: ?SessionDetails = {
          token: authData.token,
          refreshToken: authData.refreshToken,
          refreshedAt: datetime,
        };
        storeSession(session);

        return Promise.resolve(session);
      })
      .catch((err) => {
        console.error(`Session: updateToken: Error ---  ${err}`);
        return this.logout();
      });
  };

  logout = () => {
    return (this.props.client || client)
      .mutate({
        mutation: MUTATION_ADD_RETAILER_LOGS,
        variables: {
          input: {
            action: ENUM_RETAILER_LOG_ACTION.LOGOUT,
            userAgent: (navigator && navigator.userAgent) || '',
          },
        },
      })
      .then(() => {
        return (this.props.client || client).mutate({
          mutation: LOGOUT_MUTATION,
        });
      })
      .then(() => {
        return this.setState(
          { session: null, loading: false, error: null },
          () => clearStoredSession()
        );
      })
      .catch(() => {
        return this.setState(
          { session: null, loading: false, error: null },
          () => clearStoredSession()
        );
      });
  };

  render() {
    return (
      (this.state.resolved || this.state.loading) && (
        <Provider
          value={{
            ...this.state,
            login: this.login,
            logout: this.logout,
            remoteLogin: this.remoteLogin,
          }}
        >
          {this.props.children}
        </Provider>
      )
    );
  }
}

export default SessionProvider;
